import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, TableColumn } from 'src/Library/Types';
import { useGenericState } from 'src/Library/UseGenericState';
import { Table } from 'src/Library/AntdLibComponents';
import { isValidArray } from 'src/Library/Helper';
import { commonStyles } from 'src/Library/CommonStyles';
import { SplitBookingData } from '../Modals/Booking.types';
import { prettifyString } from 'src/utils/utils';
import { tokens } from 'src/design-system';

const styles = (theme) => ({
  colStyle: {
    '& .ant-table-tbody > tr > td': {
      verticalAlign: 'top',
    },
    '& .ant-table table': {
      border: '1px solid #D9D9D9',
      borderRadius: `${tokens.radius.md}px`,
      borderBottom: 0,
    },
    '& .ant-table-thead > tr > th': {
      borderBottom: '1px solid #E9E9E9',
    },
    '& .ant-table-thead > tr > th, .ant-table-tbody > tr > td': {
      padding: '8px 12px',
    },
    '& .ant-table-tbody>tr:hover:not(.ant-table-expanded-row)>td': {
      background: 'transparent',
    },
    '& .ant-table-tbody>tr, .ant-table-thead>tr': {
      transition: 'none',
    },
  },
  drawerColStyle: {
    '& .ant-table-tbody > tr > td': {
      verticalAlign: 'top',
    },
    '& .ant-table-thead > tr > th': {
      background: 'unset',
      borderBottom: 0,
    },
    '& .ant-table-thead > tr > th, .ant-table-tbody > tr > td': {
      padding: '8px 12px 8px 0px',
    },
    '& .ant-table-tbody>tr:hover:not(.ant-table-expanded-row)>td': {
      background: 'transparent',
    },
    '& .ant-table-tbody>tr, .ant-table-thead>tr': {
      transition: 'none',
    },
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '15px',
    marginRight: '6px',
  },
  showMore: {
    fontSize: `${tokens.fontSize.small}px`,
    color: tokens.colors.primary,
    cursor: 'pointer',
  },
  additionalFields: {
    color: '#424240',
    fontSize: `${tokens.fontSize.small}px`,
  },
  flexRow: {
    ...commonStyles(theme).flexRow,
  },
});

interface IProps extends StylesProps<ReturnType<typeof styles>> {
  isLoading?: boolean;
  splitBookingData: SplitBookingData [];
  isSideDrawer?: boolean;
}

interface StateProps {
  isShowMore: boolean;
  showMoreId?: string;
}

const SplitBookingDetails = (props: IProps): React.ReactElement => {

  const {
    classes,
    isLoading,
    splitBookingData,
    isSideDrawer,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    isShowMore: false,
  });

  const {
    isShowMore,
    showMoreId,
  } = state;

  const columnLabels = {
    inttraReferenceNumber: 'INTTRA Reference #',
    containerCount: 'Container Count',
    inttraStatus: 'Status',
    reasonForSplitting: 'Reason',
  };

  const renderInttraReferenceNumber = (record) => {
    return (
      <React.Fragment>
        <div>{record.inttraReferenceNumber}</div>
        {record.slBookingNumber &&
          <div style={{ marginTop: '6px', fontSize: `${tokens.fontSize.small}px` }}>SL Booking #{record.slBookingNumber}</div>}
      </React.Fragment>
    );
  };

  const renderContainerCount = (record) => {
    return (
      <React.Fragment>
        <div>{record.containerCount ||'-'}</div>
      </React.Fragment>
    );
  };

  const renderInttraStatus = (record) => {
    const { inttraStatus } = record;
    return (
      <div className={classes.flexRow}>
        <div className={classes.dot}
          style={{
            backgroundColor: inttraStatus === 'CONFIRMED' ? '#6BBA89' : 'F5222D',
          }} /> {prettifyString(inttraStatus)}
      </div>
    );
  };

  const renderCarrierComments = (record) => {
    const { carrierComments, inttraReferenceNumber } = record;
    if (!isValidArray(carrierComments)) return;
    if (isShowMore && inttraReferenceNumber === showMoreId) {
      return (
        <React.Fragment>
          <div>{carrierComments.map((item, index) => <div>{`${index + 1}. ${item}`}</div>)}</div>
          <span
            className={classes.showMore}
            onClick={() => setState({ isShowMore: false, showMoreId: undefined })}
          > Show Less</span>
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          <div>
            {carrierComments.length > 1 ?
              `1. ${carrierComments[0]}` : carrierComments[0]}
          </div>
          {(carrierComments.length > 1) && <span
            className={classes.showMore}
            onClick={() => setState({ isShowMore: true, showMoreId: inttraReferenceNumber })}
          > Show More</span>}
        </React.Fragment>
      );
    };
  };

  const renderReasonAndSLComments = (record) => {
    if (!record.reasonForSplitting && !record.carrierComments) return <div>-</div>;
    return (
      <React.Fragment>
        {record.reasonForSplitting && <div style={{ marginBottom: '8px' }}>{record.reasonForSplitting}</div>}
        <div className={classes.additionalFields}>{renderCarrierComments(record)}</div>
      </React.Fragment>
    );
  };

  const renderWidth = (key: string) : number | 'auto' => {
    switch (key) {
      case 'inttraReferenceNumber': return 220;
      case 'inttraStatus':
      case 'containerCount' : return 160;
      default: return 'auto';
    }
  };

  const getColumns = () : TableColumn<SplitBookingData> [] => {
    const columnKeys = columnLabels;
    const columns = Object.keys(columnKeys).map((key: string) => ({
      key,
      dataIndex: key,
      title: <div style={{ fontWeight: 600 }}>{columnKeys[key]}</div>,
      width: renderWidth(key),
      render: (text, record: SplitBookingData): React.ReactElement => {
        switch (key) {
          case 'inttraReferenceNumber': return renderInttraReferenceNumber(record);
          case 'containerCount': return renderContainerCount(record);
          case 'inttraStatus': return renderInttraStatus(record);
          case 'reasonForSplitting': return renderReasonAndSLComments(record);
          default: return <div></div>;
        }
      },
    }));
    return columns;
  };

  return (
    <Table
      loading={isLoading}
      pagination={false}
      dataSource={splitBookingData}
      columns={getColumns()}
      className={isSideDrawer ? classes.drawerColStyle : classes.colStyle}
    />
  );
};

export default React.memo(withStyles(styles)(SplitBookingDetails));
